/**
 * PartHierarchyComponent – parts list in the animation panel (edit mode).
 *
 * When partsHierarchyTree is provided (from buildPartsHierarchy), renders a nested tree
 * (expand/collapse, search, selection on leaves). Otherwise falls back to flat list from availableParts.
 * Data for hierarchy is fetched separately in TocEdit (not from usePartsGrid).
 * Uses @tanstack/react-virtual to render only the visible slice of the list, keeping DOM node
 * count constant (~25–30).
 */
import { useState, useMemo, useCallback, useEffect, useRef, memo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search,
  Box as BoxLucideIcon,
  ChevronRight,
  ChevronDown,
  X,
} from "lucide-mui";
import { useTheme } from "../context/ThemeContext";

const ROW_HEIGHT = 28;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function getLeafPartsUnderNode(node) {
  if (!node) return [];
  if (node.id) return [{ id: node.id, name: node.name || node.id }];
  const out = [];
  (node.children || []).forEach(child => {
    out.push(...getLeafPartsUnderNode(child));
  });
  return out;
}

// Accumulator-based DFS — avoids O(N²) array spreading in recursion.
function getDepthFirstOrderedNodes(nodes, prefix = "", acc = []) {
  (nodes || []).forEach((node, i) => {
    const pathKey = prefix ? `${prefix}-${i}` : String(i);
    acc.push({ pathKey, node });
    if (node.children?.length) {
      getDepthFirstOrderedNodes(node.children, pathKey, acc);
    }
  });
  return acc;
}

// Accumulator-based Set build — avoids creating intermediate Sets per level.
function collectAllExpandablePathKeys(nodes, prefix = "", acc = new Set()) {
  (nodes || []).forEach((node, i) => {
    const pathKey = prefix ? `${prefix}-${i}` : String(i);
    if (node.children?.length > 0) {
      acc.add(pathKey);
      collectAllExpandablePathKeys(node.children, pathKey, acc);
    }
  });
  return acc;
}

function getSearchableText(node) {
  const nameStr = node.name != null ? String(node.name) : "";
  const idStr = node.id != null ? String(node.id) : "";
  return `${nameStr} ${idStr}`.trim().toLowerCase();
}

function filterTreeByQuery(nodes, query) {
  if (!nodes?.length) return [];
  const q = (query && String(query).trim()).toLowerCase();
  if (!q) return nodes;

  function matches(node) {
    const searchable = getSearchableText(node);
    const nodeMatch = searchable.includes(q);
    if (node.id) return nodeMatch;
    if (!node.children?.length) return nodeMatch;
    const childMatch = filterTreeByQuery(node.children, query).length > 0;
    return nodeMatch || childMatch;
  }

  return nodes
    .map(node => {
      if (node.id) {
        return matches(node) ? [node] : [];
      }
      const filteredChildren = filterTreeByQuery(node.children || [], query);
      const folderSearchable = getSearchableText(node);
      if (filteredChildren.length > 0 || folderSearchable.includes(q)) {
        return [{ ...node, children: filteredChildren }];
      }
      return [];
    })
    .flat();
}

// Build the flat array of currently-visible rows from the tree, respecting expand state.
// This is what the virtualizer iterates over.
function buildVisibleRows(nodes, expanded, prefix = "", depth = 0, acc = []) {
  (nodes || []).forEach((node, i) => {
    const pathKey = prefix ? `${prefix}-${i}` : String(i);
    const isFolder = node.children?.length > 0;
    acc.push({ pathKey, node, depth, isFolder });
    if (isFolder && expanded.has(pathKey)) {
      buildVisibleRows(node.children, expanded, pathKey, depth + 1, acc);
    }
  });
  return acc;
}

// ---------------------------------------------------------------------------
// Memoized row components — only re-render when their specific props change
// ---------------------------------------------------------------------------

const TreeLeafRow = memo(function TreeLeafRow({
  node,
  depth,
  pathKey,
  selected,
  modified,
  hidden,
  isDark,
  onClick,
}) {
  return (
    <Box
      data-part-id={node.id}
      onClick={e => onClick(node, pathKey, e, false)}
      sx={{
        ml: `${depth * 18}px`,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        p: "4px 8px",
        cursor: "pointer",
        height: `${ROW_HEIGHT}px`,
        backgroundColor: selected
          ? isDark
            ? "#22332C"
            : "#E3EBE9"
          : "transparent",
        border: "1px solid",
        borderColor: selected
          ? isDark
            ? "#41D17933"
            : "#146E3799"
          : "transparent",
        transition: "background-color 0.15s ease",
      }}
    >
      <Box
        sx={{
          opacity: hidden ? 0.4 : 1,
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <BoxLucideIcon
          sx={{
            fontSize: "12px",
            color: modified ? "#c48533" : "#6A7079",
            flexShrink: 0,
          }}
        />
        <Typography
          title={node.name}
          sx={{
            color: selected
              ? isDark
                ? "#FFFFFF"
                : "#31363B"
              : isDark
                ? "#B3B3BB"
                : "#434950",
            fontFamily: "Inter",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16px",
            userSelect: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {node.name}
        </Typography>
      </Box>
    </Box>
  );
});

const TreeFolderRow = memo(function TreeFolderRow({
  node,
  depth,
  pathKey,
  isExpanded,
  selected,
  isDark,
  onToggle,
  onNodeClick,
}) {
  return (
    <Box
      sx={{
        pl: `${depth * 18}px`,
        borderRadius: "3px",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        py: "4px",
        pr: "8px",
        height: `${ROW_HEIGHT}px`,
      }}
    >
      <Box
        onClick={e => {
          e.stopPropagation();
          onToggle(pathKey);
        }}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {isExpanded ? (
          <ChevronDown
            sx={{ fontSize: "12px", color: isDark ? "#8c8c8c" : "#6B7280" }}
          />
        ) : (
          <ChevronRight
            sx={{ fontSize: "12px", color: isDark ? "#8c8c8c" : "#6B7280" }}
          />
        )}
      </Box>
      <Typography
        onClick={e => onNodeClick(node, pathKey, e, false)}
        title={node.name}
        sx={{
          color: selected ? "#12b320" : isDark ? "#DEDEDE" : "#434950",
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: "16px",
          userSelect: "none",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          cursor: "pointer",
          "&:hover": { backgroundColor: isDark ? "#252525" : "#E5E7EB" },
          borderRadius: "2px",
          padding: "2px 4px",
          margin: "-2px -4px",
        }}
      >
        {node.name}
      </Typography>
    </Box>
  );
});

const FlatRow = memo(function FlatRow({
  part,
  index,
  selected,
  modified,
  hidden,
  isDark,
  onClick,
}) {
  return (
    <Box
      data-part-id={part.id}
      onClick={e => onClick(part, index, e, true)}
      sx={{
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 8px",
        cursor: "pointer",
        height: `${ROW_HEIGHT}px`,
        backgroundColor: selected
          ? isDark
            ? "#22332C"
            : "#E3EBE9"
          : "transparent",
        border: "1px solid",
        borderColor: selected
          ? isDark
            ? "#41D17933"
            : "#146E3799"
          : "transparent",
        transition: "background-color 0.15s ease",
      }}
    >
      <Box
        sx={{
          opacity: hidden ? 0.4 : 1,
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <BoxLucideIcon
          sx={{
            fontSize: "12px",
            color: modified ? "#c48533" : "#6A7079",
            flexShrink: 0,
          }}
        />
        <Typography
          title={part.name}
          sx={{
            color: selected
              ? isDark
                ? "#FFFFFF"
                : "#31363B"
              : isDark
                ? "#B3B3BB"
                : "#434950",
            fontFamily: "Inter",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16px",
            userSelect: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {part.name}
        </Typography>
      </Box>
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PartHierarchyComponent({
  availableParts,
  partsHierarchyTree = [],
  selectedParts = [],
  handlePartsChange,
  removeSelectedPart,
  modifiedPartIds = [],
}) {
  const { isDark } = useTheme();
  const modifiedSet = useMemo(
    () => new Set((modifiedPartIds || []).map(id => String(id))),
    [modifiedPartIds],
  );

  const visibleByPartId = useMemo(() => {
    const map = new Map();
    (availableParts || []).forEach(p => {
      if (p?.id != null) map.set(String(p.id), p.visible);
    });
    return map;
  }, [availableParts]);

  const isPartHidden = useCallback(
    partOrNode => {
      if (partOrNode?.visible === false) return true;
      const id = partOrNode?.id != null ? String(partOrNode.id) : null;
      if (id == null) return false;
      return visibleByPartId.get(id) === false;
    },
    [visibleByPartId],
  );

  const isModified = useCallback(
    part => part?.id != null && modifiedSet.has(String(part.id)),
    [modifiedSet],
  );

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [lastSelectedPathKey, setLastSelectedPathKey] = useState(null);
  const [lastSelectedFlatIndex, setLastSelectedFlatIndex] = useState(null);
  const scrollContainerRef = useRef(null);
  const scrolledIdRef = useRef(null);

  useEffect(() => {
    if (partsHierarchyTree.length > 0) {
      setExpanded(collectAllExpandablePathKeys(partsHierarchyTree));
    }
  }, [partsHierarchyTree]);

  const useTree = partsHierarchyTree.length > 0;

  const filteredParts = useMemo(() => {
    const q = (searchQuery && String(searchQuery).trim()).toLowerCase();
    if (!q) return availableParts;
    return (availableParts || []).filter(p => {
      const nameStr = p.name != null ? String(p.name) : "";
      const idStr = p.id != null ? String(p.id) : "";
      return `${nameStr} ${idStr}`.trim().toLowerCase().includes(q);
    });
  }, [availableParts, searchQuery]);

  const filteredTree = useMemo(
    () => (useTree ? filterTreeByQuery(partsHierarchyTree, searchQuery) : []),
    [useTree, partsHierarchyTree, searchQuery],
  );

  // All tree nodes in DFS order (regardless of expand state) — used only for ancestor expansion.
  const depthFirstOrderedNodes = useMemo(
    () => getDepthFirstOrderedNodes(filteredTree),
    [filteredTree],
  );

  // Flat list of currently-visible rows for the virtualizer. Recomputed on expand/filter changes.
  const rowItems = useMemo(
    () => (useTree ? buildVisibleRows(filteredTree, expanded) : filteredParts),
    [useTree, filteredTree, expanded, filteredParts],
  );

  const selectedIdSet = useMemo(
    () => new Set((selectedParts || []).map(sp => String(sp.id))),
    [selectedParts],
  );

  const isSelected = useCallback(
    part => selectedIdSet.has(String(part.id)),
    [selectedIdSet],
  );

  const isNodeSelected = useCallback(
    node => {
      const parts = getLeafPartsUnderNode(node);
      if (!parts.length) return false;
      return parts.every(p => selectedIdSet.has(String(p.id)));
    },
    [selectedIdSet],
  );

  const handleNodeClick = useCallback(
    (node, pathKeyOrIndex, event, isFlat = false) => {
      const parts = getLeafPartsUnderNode(node);

      if (isFlat && node?.id) {
        const singlePart = [{ id: node.id, name: node.name || node.id }];
        if (event.shiftKey) {
          const lastIdx = lastSelectedFlatIndex;
          if (lastIdx == null) {
            handlePartsChange(singlePart);
            setLastSelectedFlatIndex(pathKeyOrIndex);
            return;
          }
          const lo = Math.min(lastIdx, pathKeyOrIndex);
          const hi = Math.max(lastIdx, pathKeyOrIndex);
          const rangeParts = rowItems
            .slice(lo, hi + 1)
            .map(p => ({ id: p.id, name: p.name || p.id }));
          handlePartsChange(rangeParts);
          setLastSelectedFlatIndex(pathKeyOrIndex);
          return;
        }
        if (event.ctrlKey || event.metaKey) {
          const isIn = (selectedParts || []).some(sp => sp.id === node.id);
          const next = isIn
            ? (selectedParts || []).filter(sp => sp.id !== node.id)
            : [...(selectedParts || []), ...singlePart];
          handlePartsChange(next);
          setLastSelectedFlatIndex(pathKeyOrIndex);
          return;
        }
        handlePartsChange(singlePart);
        setLastSelectedFlatIndex(pathKeyOrIndex);
        return;
      }

      // Tree mode — shift/ctrl use rowItems (visible rows) for range.
      if (event.shiftKey) {
        const lastKey = lastSelectedPathKey;
        if (lastKey == null) {
          handlePartsChange(parts);
          setLastSelectedPathKey(pathKeyOrIndex);
          return;
        }
        const idx = rowItems.findIndex(r => r.pathKey === pathKeyOrIndex);
        const lastIdx = rowItems.findIndex(r => r.pathKey === lastKey);
        if (idx < 0 || lastIdx < 0) {
          handlePartsChange(parts);
          setLastSelectedPathKey(pathKeyOrIndex);
          return;
        }
        const lo = Math.min(idx, lastIdx);
        const hi = Math.max(idx, lastIdx);
        const rangeParts = [];
        const seen = new Set();
        for (let i = lo; i <= hi; i++) {
          getLeafPartsUnderNode(rowItems[i].node).forEach(p => {
            if (!seen.has(p.id)) {
              seen.add(p.id);
              rangeParts.push(p);
            }
          });
        }
        handlePartsChange(rangeParts);
        setLastSelectedPathKey(pathKeyOrIndex);
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        const ids = new Set(parts.map(p => p.id));
        const anySelected = parts.some(p =>
          (selectedParts || []).some(sp => sp.id === p.id),
        );
        const next = anySelected
          ? (selectedParts || []).filter(sp => !ids.has(sp.id))
          : [...(selectedParts || []).filter(sp => !ids.has(sp.id)), ...parts];
        handlePartsChange(next);
        setLastSelectedPathKey(pathKeyOrIndex);
        return;
      }

      handlePartsChange(parts);
      setLastSelectedPathKey(pathKeyOrIndex);
    },
    [
      handlePartsChange,
      selectedParts,
      lastSelectedPathKey,
      lastSelectedFlatIndex,
      rowItems,
    ],
  );

  const toggleExpand = useCallback(pathKey => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  }, []);

  const firstSelectedId =
    selectedParts?.length > 0 && selectedParts[0]?.id != null
      ? String(selectedParts[0].id)
      : null;

  // Expand ancestor folders when selection changes so the selected item becomes visible.
  useEffect(() => {
    if (!firstSelectedId || !useTree || !depthFirstOrderedNodes.length) return;
    const entry = depthFirstOrderedNodes.find(
      e => e.node?.id != null && String(e.node.id) === firstSelectedId,
    );
    if (!entry) return;
    scrolledIdRef.current = null; // trigger scroll after expand re-render
    const pathParts = entry.pathKey.split("-");
    const prefixPathKeys = pathParts.map((_, i) =>
      pathParts.slice(0, i + 1).join("-"),
    );
    setExpanded(prev => {
      const next = new Set(prev);
      let changed = false;
      prefixPathKeys.forEach(k => {
        if (!next.has(k)) {
          next.add(k);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [firstSelectedId, useTree, depthFirstOrderedNodes]);

  // Virtualizer setup — scroll container is the Box with ref={scrollContainerRef}.
  const virtualizer = useVirtualizer({
    count: rowItems.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  // Scroll to selected item once it appears in rowItems (after ancestor expansion).
  useEffect(() => {
    if (!firstSelectedId) return;
    if (scrolledIdRef.current === firstSelectedId) return;
    let idx = -1;
    if (useTree) {
      idx = rowItems.findIndex(
        r => r.node?.id != null && String(r.node.id) === firstSelectedId,
      );
    } else {
      idx = rowItems.findIndex(
        r => r.id != null && String(r.id) === firstSelectedId,
      );
    }
    if (idx >= 0) {
      virtualizer.scrollToIndex(idx, { align: "auto", behavior: "smooth" });
      scrolledIdRef.current = firstSelectedId;
    }
  }, [firstSelectedId, rowItems, useTree, virtualizer]);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: isDark ? "#1F2123" : "#ffffff",
        padding: "12px",
        fontFamily: "Inter",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "6px",
        }}
      >
        <Typography
          sx={{
            color: isDark ? "#B3B3BB" : "#6A7079",
            fontFamily: "Inter",
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: 500,
          }}
        >
          PARTS
        </Typography>
        {selectedParts.length > 0 && (
          <Typography
            sx={{
              color: isDark ? "#A8AEB6" : "#6A7079",
              bgcolor: isDark ? "#31363B" : "#D5D9DF",
              fontFamily: "Inter",
              fontSize: "11px",
              fontWeight: 400,
              lineHeight: "14px",
              letterSpacing: "-0.15px",
              p: "4px 8px",
              borderRadius: "10px",
            }}
          >
            {selectedParts.length} selected
          </Typography>
        )}
      </Box>

      <TextField
        fullWidth
        placeholder='Type part name...'
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing)
            setSearchQuery(searchInput || "");
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment
              position='end'
              sx={{ "& svg": { width: "16px", height: "16px" } }}
            >
              {searchInput ? (
                <IconButton
                  aria-label='Clear search'
                  size='small'
                  onClick={() => {
                    setSearchInput("");
                    setSearchQuery("");
                  }}
                  sx={{ p: 0, color: isDark ? "#A8AEB6" : "#6A7079" }}
                >
                  <X />
                </IconButton>
              ) : (
                <Search style={{ color: isDark ? "#A8AEB6" : "#6A7079" }} />
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "32px",
            backgroundColor: isDark ? "#1F2123" : "#FFFFFF",
            borderRadius: "12px",
            color: isDark ? "#DEDEDE" : "#434950",
            fontFamily: "Inter",
            fontSize: "14px",
            "& fieldset": {
              borderColor: isDark
                ? "rgba(106,112,121,0.5)"
                : "rgba(106,112,121,0.3)",
            },
            "&:hover fieldset": {
              borderColor: isDark
                ? "rgba(106,112,121,0.7)"
                : "rgba(106,112,121,0.5)",
            },
            "&.Mui-focused fieldset": {
              borderColor: isDark
                ? "rgba(106,112,121,0.7)"
                : "rgba(106,112,121,0.5)",
            },
          },
          "& .MuiInputBase-input": {
            color: isDark ? "#DEDEDE" : "#434950",
            padding: "8px 12px",
            boxSizing: "border-box",
            "&::placeholder": {
              fontStyle: "italic",
              opacity: 1,
              color: "#A8AEB6",
              fontSize: "14px",
              lineHeight: "20px",
              fontFamily: "Inter",
              fontWeight: 400,
            },
          },
        }}
      />

      <Box
        ref={scrollContainerRef}
        sx={{
          mt: "4px",
          overflow: "auto",
          flex: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "#31363B" : "#D5D9DF",
            borderRadius: "3px",
          },
        }}
      >
        {rowItems.length === 0 ? (
          <Typography
            sx={{
              color: isDark ? "#FFFFFF" : "#434950",
              fontFamily: "Inter",
              fontSize: "11px",
              padding: "2px 6px",
              opacity: 0.9,
            }}
          >
            No options
          </Typography>
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map(virtualItem => {
              const row = rowItems[virtualItem.index];
              return (
                <div
                  key={virtualItem.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {useTree ? (
                    row.isFolder ? (
                      <TreeFolderRow
                        node={row.node}
                        depth={row.depth}
                        pathKey={row.pathKey}
                        isExpanded={expanded.has(row.pathKey)}
                        selected={isNodeSelected(row.node)}
                        isDark={isDark}
                        onToggle={toggleExpand}
                        onNodeClick={handleNodeClick}
                      />
                    ) : (
                      <TreeLeafRow
                        node={row.node}
                        depth={row.depth}
                        pathKey={row.pathKey}
                        selected={isNodeSelected(row.node)}
                        modified={isModified({ id: row.node.id })}
                        hidden={isPartHidden(row.node)}
                        isDark={isDark}
                        onClick={handleNodeClick}
                      />
                    )
                  ) : (
                    <FlatRow
                      part={row}
                      index={virtualItem.index}
                      selected={isSelected(row)}
                      modified={isModified(row)}
                      hidden={isPartHidden(row)}
                      isDark={isDark}
                      onClick={handleNodeClick}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Box>
    </Box>
  );
}
